<!-- 
	This is the sign in page, it uses the dashboard layout in: 
	"./layouts/Default.vue" .
 -->

<template>
	<div class="sign-in">
		
		<a-row type="flex" :gutter="[24,24]" justify="space-around" align="middle">

			<!-- Sign In Form Column -->
			<a-col :span="24" :md="12" :lg="{span: 12, offset: 0}" :xl="{span: 6, offset: 2}" class="col-form">
				<h1 class="mb-15">Sign In</h1>
				<h5 class="font-regular text-muted">Enter your email and password to sign in</h5>

				<!-- Sign In Form -->
				<a-form
					id="components-form-demo-normal-login"
					:form="form"
					class="login-form"
					@submit="handleSubmit"
					:hideRequiredMark="true"
				>
					<a-form-item class="mb-10" label="Email" :colon="false">
						<a-input
						v-decorator="[
						'email',
						{ rules: [
							{ required: true, message: 'Please input your email!' },
							{ type: 'email', message: 'Please enter a valid email address!' }
						] },
						]" placeholder="Email" type="email" />
					</a-form-item>
					<a-form-item class="mb-5" label="Password" :colon="false">
						<a-input
						v-decorator="[
						'password',
						{ rules: [{ required: true, message: 'Please input your password!' }] },
						]" type="password" placeholder="Password" />
					</a-form-item>
					<div class="text-right mb-15">
						<router-link to="/forgot-password" class="text-muted">Forgot password?</router-link>
					</div>
					<a-form-item>
						<a-button type="primary" block html-type="submit" class="login-form-button">
							SIGN IN
						</a-button>
					</a-form-item>

					<div class="text-center">
						<span class="text-muted">Don't have an account? </span>
						<router-link to="/sign-up" class="font-bold">Sign Up</router-link>
					</div>
					<div class="text-center mt-15">
						<router-link to="/" class="text-muted">Back to Home</router-link>
					</div>
				</a-form>
				<!-- / Sign In Form -->
			</a-col>
			<!-- / Sign In Form Column -->

			<!-- Sign In Image Column -->
			<a-col :span="24" :md="12" :lg="12" :xl="12" class="col-img">
				<img src="images/zerocents-logo.png" alt="">
			</a-col>
			<!-- Sign In Image Column -->

		</a-row>
		
	</div>
</template>

<script>
	import { OrganizationManager } from '../auth.js';

	export default ({
		data() {
			return {
			}
		},
		beforeCreate() {
			// Creates the form and adds to it component's "form" property.
			this.form = this.$form.createForm(this, { name: 'normal_login' });
		},
		methods: {
			// Handles input validation after submission.
			async handleSubmit(e) {
				e.preventDefault();
				this.form.validateFields(async (err, values) => {
					if (!err) {
						try {
							// Login request
							const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
							const response = await fetch(`${apiBaseUrl}/api/auth/login`, {
								method: 'POST',
								headers: {
									'Content-Type': 'application/json',
								},
								body: JSON.stringify({
									email: values.email,
									password: values.password,
								}),
							});
							if (!response.ok) {
								throw new Error('Invalid credentials');
							}
							const data = await response.json();

							// Store initial token
							localStorage.setItem('token', data.token);

							// Check for pending invitation
							const pendingInvitationToken = localStorage.getItem('pendingInvitationToken');
							if (pendingInvitationToken) {
								try {
									// Accept invitation for existing user
									const invitationResponse = await fetch(`${apiBaseUrl}/api/auth/accept-invitation-existing-user`, {
										method: 'POST',
										headers: {
											'Content-Type': 'application/json',
											'Authorization': `Bearer ${data.token}`
										},
										body: JSON.stringify({ token: pendingInvitationToken })
									});

									if (invitationResponse.ok) {
										const invitationData = await invitationResponse.json();
										// Update token with organization context
										localStorage.setItem('token', invitationData.token);
										OrganizationManager.setSelectedOrganizationId(invitationData.organizationId);

										// Clear pending invitation
										localStorage.removeItem('pendingInvitationToken');

										this.$message.success('Successfully joined the organization!');
										this.$router.push('/dashboard');
										return;
									} else {
										console.error('Failed to accept invitation');
										localStorage.removeItem('pendingInvitationToken');
									}
								} catch (invitationError) {
									console.error('Error accepting invitation:', invitationError);
									localStorage.removeItem('pendingInvitationToken');
								}
							}

							// Fetch user's organizations
							const organizations = await OrganizationManager.fetchUserOrganizations();

							// Filter to only enabled organizations
							const enabledOrgs = organizations ? organizations.filter(org => org.enabled !== false) : [];

							// If user has enabled organizations, try to select one
							if (enabledOrgs.length > 0) {
								let selectedOrg = false;

								// Try to select an organization
								for (const org of enabledOrgs) {
									try {
										await OrganizationManager.selectOrganization(org.id);
										selectedOrg = true;
										break;
									} catch (orgError) {
										console.log(`Could not select organization ${org.id}:`, orgError.message);
										// Continue to try next organization
									}
								}

								if (selectedOrg) {
									// Successfully selected an organization - go to dashboard
									this.$router.push('/dashboard');
								} else {
									// Could not select any organization - clear selected org and redirect
									localStorage.removeItem('selectedOrganizationId');
									this.$router.push('/create-organization');
								}
							} else {
								// No enabled organizations - clear selected org and redirect to create organization
								localStorage.removeItem('selectedOrganizationId');
								this.$router.push('/create-organization');
							}
						} catch (error) {
							console.error('Login error:', error);
							this.$message.error(error.message || 'Login failed');
						}
					}
				});
			},
		},
	})

</script>

<style lang="scss" scoped>
	body {
		background-color: #ffffff;
	}

	.sign-in {
		min-height: 100vh;
		display: flex;
		align-items: center;
		background-color: #ffffff;
		padding: 0 20px;

		.text-center {
			text-align: center;
		}

		.text-right {
			text-align: right;
		}

		.font-bold {
			font-weight: 600;
			color: #1890ff;

			&:hover {
				color: #40a9ff;
			}
		}
	}

	.col-img {
		img {
			max-width: 100%;
			height: auto;
			display: block;
		}
	}
</style>